import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Button,
  Alert,
  Divider,
} from '@mui/material';
import { ExpandMore, CheckCircle, Error } from '@mui/icons-material';
import { Copy } from 'lucide-react';
import { Auth } from '@billda/permissions-ui';
import { useAuthStore } from '../../../features/auth/stores/authStore';

export const JwtDebugSection: React.FC = () => {
  const token = useAuthStore(state => state.token);
  const [parsedToken, setParsedToken] = useState<any | null>(null);
  const [timeUntilExpiry, setTimeUntilExpiry] = useState<string>('');
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  useEffect(() => {
    if (token) {
      const parsed = Auth.parseToken(token);
      setParsedToken(parsed);
    } else {
      setParsedToken(null);
    }
  }, [token]);

  // Decode raw JWT payload claims for debug display and test selectors
  const rawClaims = React.useMemo(() => {
    if (!token) return null;
    try {
      const parts = token.split('.');
      if (parts.length < 2) return null;
      const payloadBase64 = parts[1];
      const normalized = payloadBase64
        .replace(/-/g, '+')
        .replace(/_/g, '/')
        .padEnd(
          payloadBase64.length + ((4 - (payloadBase64.length % 4)) % 4),
          '='
        );
      const json = atob(normalized);
      return JSON.parse(json) as Record<string, any>;
    } catch {
      return null;
    }
  }, [token]);

  useEffect(() => {
    // Update countdown timer
    const expDate = parsedToken?.expiresAt as Date | undefined;
    if (expDate) {
      const updateTimer = () => {
        const now = Date.now();
        const diff = Math.floor((expDate.getTime() - now) / 1000);
        if (diff <= 0) {
          setTimeUntilExpiry('Expired');
          return;
        }
        const days = Math.floor(diff / (24 * 60 * 60));
        const hours = Math.floor((diff % (24 * 60 * 60)) / (60 * 60));
        const minutes = Math.floor((diff % (60 * 60)) / 60);
        const seconds = diff % 60;
        if (days > 0) setTimeUntilExpiry(`${days}d ${hours}h ${minutes}m`);
        else if (hours > 0)
          setTimeUntilExpiry(`${hours}h ${minutes}m ${seconds}s`);
        else if (minutes > 0) setTimeUntilExpiry(`${minutes}m ${seconds}s`);
        else setTimeUntilExpiry(`${seconds}s`);
      };

      updateTimer();
      const interval = setInterval(updateTimer, 1000);
      return () => clearInterval(interval);
    }
  }, [parsedToken]);

  const handleCopy = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopySuccess(label);
      setTimeout(() => setCopySuccess(null), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };

  if (!token) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        No authentication token found in localStorage
      </Alert>
    );
  }

  if (!parsedToken) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Failed to decode JWT token - token may be malformed
      </Alert>
    );
  }

  const expired = token ? Auth.isTokenExpired(token) : false;

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        JWT Token Information
      </Typography>

      {/* Token Status */}
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            flexWrap: 'wrap',
          }}
        >
          <Chip
            icon={expired ? <Error /> : <CheckCircle />}
            label={expired ? 'Expired' : 'Valid'}
            color={expired ? 'error' : 'success'}
            variant="outlined"
          />
          {parsedToken.expiresAt && (
            <Typography variant="body2" color="text.secondary">
              {expired ? 'Expired' : 'Expires in'}:{' '}
              <strong>{timeUntilExpiry}</strong>
            </Typography>
          )}
        </Box>
      </Box>

      {/* Token Payload */}
      <Accordion sx={{ mb: 2 }} data-testid="jwt-payload-accordion">
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1">Payload Claims</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Box sx={{ mb: 2 }} data-testid="jwt-claims-list">
            {Object.entries(rawClaims ?? {}).map(([key, value]) => (
              <Box key={key} sx={{ mb: 1 }} data-testid={`jwt-claim-${key}`}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                  <Box sx={{ minWidth: '180px', fontWeight: 'bold' }}>
                    <Typography variant="body2" fontWeight="bold">
                      {key}:
                    </Typography>
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant="body2"
                      sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}
                      data-testid={`jwt-claim-${key}-value`}
                    >
                      {typeof value === 'object' && value !== null
                        ? JSON.stringify(value, null, 2)
                        : String(value)}
                    </Typography>
                  </Box>
                </Box>
                <Divider sx={{ mt: 1 }} />
              </Box>
            ))}
          </Box>
          <Button
            size="small"
            startIcon={<Copy />}
            onClick={() =>
              handleCopy(JSON.stringify(rawClaims ?? {}, null, 2), 'Payload')
            }
          >
            {copySuccess === 'Payload' ? 'Copied!' : 'Copy All Claims'}
          </Button>
        </AccordionDetails>
      </Accordion>

      {/* Raw Token */}
      <Accordion>
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Typography variant="subtitle1">Raw Token</Typography>
        </AccordionSummary>
        <AccordionDetails>
          <Paper
            variant="outlined"
            sx={{
              p: 2,
              bgcolor: 'background.default',
              border: 1,
              borderColor: 'divider',
            }}
          >
            <Typography
              variant="body2"
              data-testid="raw-token-value"
              sx={{
                fontFamily: 'monospace',
                wordBreak: 'break-all',
                color: 'text.primary',
              }}
            >
              {token}
            </Typography>
            <Button
              size="small"
              startIcon={<Copy />}
              onClick={() => handleCopy(token, 'Token')}
              sx={{ mt: 1 }}
            >
              {copySuccess === 'Token' ? 'Copied!' : 'Copy Token'}
            </Button>
          </Paper>
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};
